import React, {useMemo, useState} from 'react';
import {Empty, Input, Modal, Select} from "antd";
import {useDebounce} from "react-use";
import {useTalentTeam} from "../../../providers/TalentTeamProvider";
import {useAuth} from "../../../providers/AuthProvider";
import {useAPI} from "../../../config/API";
import UserImageCircle from "../../../components/UserImageCircle";
import LoadingSpinner from "../../../components/LoadingSpinner";

/**
 * Minimum characters before we hit the search endpoint. Matches the behaviour
 * of InviteTeamMemberModal so the two searches feel identical to the user.
 */
const MIN_SEARCH_LENGTH = 3;

/** Debounce (ms) applied to both the search term and the loading indicator. */
const SEARCH_DEBOUNCE_MS = 400;

/** Max length of the optional team position title. */
const MAX_TITLE_LENGTH = 100;

/**
 * Empty state rendered inside the Select dropdown.
 *
 * Unlike the invite flow there is no "invite by email" fallback here — a manual
 * add can only target talent that already exists and is active on Wripple.
 */
const NotFoundContent = ({search, isLoading}) => {

    if (!search?.length) {
        return null;
    }

    if (search.length < MIN_SEARCH_LENGTH) {
        return (
            <p className="text-xs text-center text-info py-2">
                Type at least {MIN_SEARCH_LENGTH} characters to search.
            </p>
        );
    }

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center p-3">
                <LoadingSpinner size="sm"/>
            </div>
        );
    }

    return (
        <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={(
                <p className="px-10 mx-auto whitespace-pre-line text-center">
                    {"No active talent found for\n"}
                    <span className="text-info text-underline">{search}</span>.
                </p>
            )}
        />
    );
};

/**
 * Talent-manager-only modal for placing an existing talent user directly onto a
 * team, with no invite and no acceptance required by the talent.
 *
 * It borrows the server-side talent search from InviteTeamMemberModal (which
 * already excludes current members and outstanding invites) and the add request
 * from the supplier-facing AddTeamMemberModal.
 *
 * Renders nothing for any user who is not a talent manager, and for teams
 * supplied by a client — the search endpoint rejects those outright.
 *
 * @param {string} [props.buttonLabel]     Override for the trigger button text.
 * @param {string} [props.buttonClassName] Override for the trigger button classes.
 */
function AdminAddTeamMemberModal(props) {
    const {auth} = useAuth();
    const {team, isLoading, isUpdating, addTalentMember} = useTalentTeam();

    const [modalOpen, setModalOpen] = useState(false);
    // Raw text typed into the Select.
    const [search, setSearch] = useState('');
    // Debounced copy of `search` — this is what actually drives the query.
    const [debounceInput, setDebounceInput] = useState('');
    // Debounced copy of `isFetching`, to avoid a flickering spinner.
    const [debounceLoading, setDebounceLoading] = useState(false);
    // Label shown in the closed Select once a person has been picked.
    const [selectedLabel, setSelectedLabel] = useState(undefined);
    // The chosen User model, or undefined while still searching.
    const [selectedTalent, setSelectedTalent] = useState(undefined);
    // Optional team position title applied to the new member. Seeded from the
    // selected talent's own job title, and editable before confirming.
    const [title, setTitle] = useState('');

    const canAdminAdd = auth.canManageTalent() && !team?.is_supplied_by_client;

    // The search key is shared with InviteTeamMemberModal on purpose: when both
    // modals are on screen they dedupe onto a single cached request.
    const searchQueryKey = useMemo(
        () => ['searchAllTalentForMemberInvite', {team, search: debounceInput}],
        [team, debounceInput]
    );

    useDebounce(() => setDebounceInput(search), SEARCH_DEBOUNCE_MS, [search]);

    const {data, isFetching} = useAPI(searchQueryKey, {
        enabled: canAdminAdd
            && modalOpen
            && debounceInput?.length >= MIN_SEARCH_LENGTH
            && !isUpdating
            && selectedTalent === undefined,
    });

    useDebounce(() => setDebounceLoading(isFetching), SEARCH_DEBOUNCE_MS, [isFetching]);

    // True while the user has typed something we haven't finished searching for.
    const isSearching = search !== debounceInput || isFetching || debounceLoading;

    const searchOptions = useMemo(() => {
        if (!search?.length) return [];

        return TB.map(data || [], talent => ({
            value: talent.id,
            display: talent.fullName?.(),
            label: (
                <div className="flex flex-row items-center justify-between">
                    <div className="flex flex-row items-center gap-2">
                        <UserImageCircle user={talent} size={40}/>
                        <p className="text-sm">{talent.fullName?.()}</p>
                    </div>
                    {talent.location?.city && (
                        <p className="text-xxs text-info text-right whitespace-nowrap">
                            {talent.location?.city}, {talent.location?.state}
                        </p>
                    )}
                </div>
            ),
        }));
    }, [search, data]);

    const toggleModal = () => setModalOpen(open => !open);

    /** Move from the search step to the confirmation step. */
    const onTalentSelected = (userId) => {
        const selected = TB.find(data || [], ['id', userId]);
        if (!selected) return;

        setSelectedLabel(selected.fullName?.());
        setSelectedTalent(selected);
        // Default the position title to the talent's own job title. Truncated to
        // the field's limit so the value never fails validation on submit.
        setTitle(TB.truncate(selected.job_title || '', {length: MAX_TITLE_LENGTH, omission: ''}));
    };

    /** Return to the search step without closing the modal. */
    const onClearSelection = () => {
        setSelectedTalent(undefined);
        setSelectedLabel(undefined);
        setTitle('');
    };

    /** Reset every piece of local state once the modal has finished closing. */
    const onAfterClose = async () => {
        await TB.queryClient.invalidateQueries(searchQueryKey);
        setSearch('');
        setDebounceInput('');
        onClearSelection();
    };

    const onConfirmAdd = async () => {
        const result = await addTalentMember(selectedTalent.id, title);
        if (result) {
            toggleModal();
        }
    };

    if (!canAdminAdd) {
        return null;
    }

    return (
        <React.Fragment>
            <button
                className={props.buttonClassName || "btn btn-primary"}
                onClick={toggleModal}
                disabled={!team.can_manage || isUpdating || isLoading}>
                <i className="fas fa-user-plus mr-2 text-white"/>
                {props.buttonLabel || "Add Team Member"}
            </button>

            <Modal
                destroyOnClose={true}
                open={modalOpen}
                afterClose={onAfterClose}
                closable={false}
                title={null}
                footer={null}
            >
                <div className="talent-team-member-invite">

                    <div className="self-stretch flex flex-col items-stretch gap-4">
                        <div className={TB.mapClass("h-[16px] mt-[-24px] mx-[-24px]", {
                            "bg-gray": !selectedTalent,
                            "bg-success": !!selectedTalent,
                        })}/>
                        <p className="text-2xl font-medium">
                            {selectedTalent ? "Confirm Team Member" : "Add Team Member"}
                        </p>
                    </div>

                    <hr className="my-4 mx-[-24px]"/>

                    {/* Step 1 — find the talent. */}
                    {!selectedTalent && (
                        <React.Fragment>
                            <p className="mb-4">
                                Search active talent on Wripple by name or email. The selected
                                person will be added to this team immediately — no invite is
                                sent and no acceptance is required.
                            </p>
                            <Select
                                style={{width: '100%'}}
                                name="admin_search_talent"
                                placeholder="Search by Name or Email"
                                maxLength={255}
                                showSearch
                                options={searchOptions}
                                optionLabelProp="display"
                                defaultActiveFirstOption={false}
                                filterOption={false}
                                disabled={isUpdating}
                                value={selectedLabel}
                                onSearch={setSearch}
                                onChange={onTalentSelected}
                                notFoundContent={(
                                    <NotFoundContent search={search} isLoading={isSearching}/>
                                )}
                                loading={isSearching}
                            />
                        </React.Fragment>
                    )}

                    {/* Step 2 — confirm, and optionally set a position title. */}
                    {selectedTalent && (
                        <div className="flex flex-col gap-3">
                            <p>
                                <span className="text-[#5f3db1] font-semibold">{selectedLabel}</span> will
                                be added to <b>{team.name}</b> as a team member.
                            </p>

                            <p>Name: <b>{selectedTalent.fullName?.()}</b></p>

                            {selectedTalent.location?.city && (
                                <p>
                                    Location: <b>{selectedTalent.location?.city}, {selectedTalent.location?.state}</b>
                                </p>
                            )}

                            <p>Roles on Wripple:</p>
                            <ul>
                                {selectedTalent.disciplines?.length > 0 ? (
                                    TB.map(selectedTalent.disciplines, (role, rIndex) => (
                                        <span className="text-[#707683] font-semibold" key={role.id}>
                                            {role.discipline?.name}
                                            {rIndex < selectedTalent.disciplines.length - 1 && <span>,&nbsp;</span>}
                                        </span>
                                    ))
                                ) : <span>None</span>}
                            </ul>

                            <div className="flex flex-col gap-1">
                                <p>Team Position Title <span className="text-info text-xs">(optional)</span>:</p>
                                <Input.TextArea
                                    autoFocus={true}
                                    value={title}
                                    size="small"
                                    showCount
                                    maxLength={MAX_TITLE_LENGTH}
                                    className="text-xs"
                                    autoSize
                                    disabled={isUpdating}
                                    onChange={(e) => setTitle(e.target.value)}
                                />
                            </div>

                            <button
                                className="btn btn-link btn-link-info p-0 self-start"
                                disabled={isUpdating}
                                onClick={onClearSelection}>
                                Choose someone else
                            </button>
                        </div>
                    )}

                    <div className="flex flex-row gap-3 justify-end mt-[50px]">
                        <button className="btn btn-outline-dark w-[100px]"
                                disabled={isUpdating}
                                onClick={toggleModal}>
                            Cancel
                        </button>

                        {selectedTalent && (
                            <button className="btn btn-primary"
                                    disabled={isUpdating}
                                    onClick={onConfirmAdd}>
                                Add Member
                            </button>
                        )}
                    </div>
                </div>
            </Modal>
        </React.Fragment>
    );
}

export default AdminAddTeamMemberModal;
